import { useState, useEffect, useMemo } from 'react';
import { QuickstartPhase } from '../QuickstartController';
import { QuickstartTheme, isGameReleased } from '../types/theme';
import { PHASE_DISPLAY_NAMES } from '@playcastdotio/Messaging';
import type { FlowGraph, FlowNode } from '@playcastdotio/Messaging';
import { fetchFlowGraph } from '../../../api/modules/campaigns';

interface QuickstartDebugOverlayProps {
  currentPhase: QuickstartPhase;
  currentTheme?: QuickstartTheme;
  sessionId?: string;
  campaignId?: string;
  onSelectPhase: (phase: QuickstartPhase) => void;
  onSetReleaseTimestamp?: (timestamp: number | 'past' | 'future') => void;
}

const QUICKSTART_DEBUG_STORAGE_KEY = 'quickstart-debug-overlay-collapsed';

const FALLBACK_PHASE_GROUPS: Record<string, { phase: string; label: string }[]> = {
  Initial: [
    { phase: 'page_viewed', label: '1. Page Viewed' },
    { phase: 'environment_check', label: '2. Environment Check' },
    { phase: 'embedded_browser_blocked', label: '3. Embedded Browser' },
  ],
  Matchmaking: [
    { phase: 'creating_lobby', label: '4. Creating Lobby' },
    { phase: 'matchmaking', label: '5. Matchmaking' },
    { phase: 'matchmaking_failed', label: '5a. Failed ❌' },
    { phase: 'match_found', label: '6. Match Found ✓' },
  ],
  Connection: [
    { phase: 'joining_broadcast', label: '7. Joining' },
    { phase: 'broadcast_join_failed', label: '7a. Failed ❌' },
    { phase: 'preparing_player', label: '7b. Preparing Player' },
  ],
  Playing: [
    { phase: 'playing_afk', label: '8. Playing (AFK)' },
    { phase: 'playing_active', label: '9. Playing (Active)' },
  ],
  Exit: [
    { phase: 'afk_timeout', label: '10. AFK Timeout' },
    { phase: 'ad_timer_expired', label: '11. Timer Expired' },
    { phase: 'wishlist_early_exit', label: '12. Wishlist Exit' },
    { phase: 'thank_you', label: '13. Thank You (Unreleased)' },
    { phase: 'thank_you_released', label: '13b. Thank You (Released) 🎮' },
    { phase: 'auto_redirect', label: '13c. Auto-Redirect 🚀' },
  ],
  Error: [{ phase: 'error', label: 'Error State' }],
  'Legacy Auth Flow (Deprecated)': [
    { phase: 'cta_page', label: 'CTA (Unreleased)' },
    { phase: 'cta_page_released', label: 'CTA (Released) 🎮' },
    { phase: 'auth_attempted', label: 'Auth' },
    { phase: 'auth_failed', label: 'Auth Failed ❌' },
  ],
};

function buildPhaseGroupsFromGraph(graph: FlowGraph): Record<string, { phase: string; label: string }[]> {
  const sorted = [...graph.nodes].sort((a, b) => a.position.x - b.position.x);
  const phases = sorted.map((node: FlowNode, i: number) => ({
    phase: node.phase,
    label: `${i + 1}. ${PHASE_DISPLAY_NAMES[node.phase] ?? node.phase}`,
  }));
  return { 'Flow Graph': phases };
}

export const QuickstartDebugOverlay = ({
  currentPhase,
  currentTheme,
  sessionId,
  campaignId,
  onSelectPhase,
  onSetReleaseTimestamp,
}: QuickstartDebugOverlayProps) => {
  const [flowGraph, setFlowGraph] = useState<FlowGraph | null>(null);

  useEffect(() => {
    if (!campaignId) return;
    fetchFlowGraph(campaignId).then(setFlowGraph);
  }, [campaignId]);

  const phaseGroups = useMemo(() => {
    if (flowGraph?.nodes?.length) return buildPhaseGroupsFromGraph(flowGraph);
    return FALLBACK_PHASE_GROUPS;
  }, [flowGraph]);

  const handlePhaseClick = (phase: string) => {
    if (phase === 'cta_page_released') {
      onSetReleaseTimestamp?.('past');
      onSelectPhase('cta_page' as QuickstartPhase);
    } else if (phase === 'cta_page') {
      onSetReleaseTimestamp?.('future');
      onSelectPhase(phase as QuickstartPhase);
    } else if (phase === 'thank_you_released') {
      onSetReleaseTimestamp?.('past');
      onSelectPhase('thank_you' as QuickstartPhase);
    } else if (phase === 'thank_you') {
      onSetReleaseTimestamp?.('future');
      onSelectPhase(phase as QuickstartPhase);
    } else {
      onSelectPhase(phase as QuickstartPhase);
    }
  };

  const [isCollapsed, setIsCollapsed] = useState(() => {
    try {
      const saved = localStorage.getItem(QUICKSTART_DEBUG_STORAGE_KEY);
      return saved === 'true';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(QUICKSTART_DEBUG_STORAGE_KEY, String(isCollapsed));
    } catch (error) {
      console.warn('[QuickstartDebugOverlay] Failed to save state:', error);
    }
  }, [isCollapsed]);

  return (
    <div className='fixed right-0 top-0 z-50 flex h-screen items-center'>
      <button
        onClick={() => setIsCollapsed(!isCollapsed)}
        className='z-[3000] mr-2 rounded-l-lg bg-purple-600 px-2 py-8 font-mono text-xs font-bold text-white shadow-lg hover:bg-purple-700'
      >
        {isCollapsed ? '◀' : '▶'}
      </button>

      {!isCollapsed && (
        <div className='h-full w-80 overflow-y-auto bg-gray-900 p-4 shadow-2xl'>
          <div className='mb-4'>
            <h3 className='mb-1 font-mono text-sm font-bold text-white'>
              <span role='img' aria-label='bug'>🐛</span>{' '}Debug Panel
            </h3>
            {flowGraph && (
              <p className='font-mono text-[10px] text-green-400'>Using saved flow graph</p>
            )}
          </div>

          {sessionId && (
            <div className='mb-4 rounded border border-blue-600/50 bg-blue-900/30 p-2'>
              <div className='mb-1 flex items-center justify-between'>
                <p className='font-mono text-xs font-semibold text-blue-200'>Session ID</p>
                <button
                  onClick={() => { navigator.clipboard.writeText(sessionId); }}
                  className='rounded bg-blue-700/50 px-2 py-0.5 font-mono text-xs text-blue-100 hover:bg-blue-600/50'
                  title='Copy to clipboard'
                >
                  Copy
                </button>
              </div>
              <p className='break-all font-mono text-xs text-blue-300'>{sessionId}</p>
            </div>
          )}

          <div className='space-y-3'>
            {Object.entries(phaseGroups).map(([groupName, phases]) => (
              <div key={groupName}>
                <h4 className='mb-1 font-mono text-xs font-semibold text-gray-500'>{groupName}</h4>
                <div className='space-y-1'>
                  {phases.map(({ phase, label }) => {
                    let isActive = phase === currentPhase;

                    if (currentTheme) {
                      const gameIsReleased = isGameReleased(currentTheme);

                      if (currentPhase === 'cta_page') {
                        if (phase === 'cta_page') {
                          isActive = !gameIsReleased;
                        } else if (phase === 'cta_page_released') {
                          isActive = gameIsReleased;
                        }
                      }

                      if (currentPhase === 'thank_you') {
                        if (phase === 'thank_you') {
                          isActive = !gameIsReleased;
                        } else if (phase === 'thank_you_released') {
                          isActive = gameIsReleased;
                        }
                      }
                    }

                    return (
                      <button
                        key={phase}
                        onClick={() => handlePhaseClick(phase)}
                        className={`w-full rounded px-2 py-1.5 text-left font-mono text-xs transition-all ${
                          isActive ? 'bg-green-600 font-bold text-white' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                        }`}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          <div className='mt-4 rounded border border-yellow-600/50 bg-yellow-900/30 p-2'>
            <p className='font-mono text-xs text-yellow-200'>
              <span role='img' aria-label='warning'>⚠️</span>{' '}Analytics still fire
            </p>
          </div>

          <div className='mt-3 text-center'>
            <p className='font-mono text-xs text-gray-500'>
              Remove <code className='rounded bg-gray-800 px-1'>?debug</code> to exit
            </p>
          </div>
        </div>
      )}
    </div>
  );
};
